import { createElement, useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { CreateEditEntityTemplateDialogProps } from "./CreateEditEntityTemplateDialog.tsx";
import { EntityTemplateRowViewModel, getEntityTemplateTranslationKeys, TemplateEntityType } from "../EntityTemplate.types.ts";
import { Trans } from "react-i18next";
import { isNullOrWhitespace } from "../../common/stringOperations.ts";
import { post, put } from "../../common/api/apiShared.ts";
import { useUrlProvider } from "../../useUrlProvider.ts";
import { useNavigate } from "react-router";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "../../localization/useTranslation.ts";
import { useSelectableStandardEntityTemplates } from "@common/hooks/useSelectableItemQueries";
import { useEntityTemplateLockedText } from "../useEntityTemplateLockedText";

interface EntityTemplateSaveModel {
    name: string;
    description?: string;
    standardTemplateId?: string;
}

export function useCreateEditEntityTemplateDialog(props: CreateEditEntityTemplateDialogProps) {
    const snackbar = useDotLegalSnackbar();
    const urlProvider = useUrlProvider();
    const navigate = useNavigate();
    const { translateString } = useTranslation();
    const { getLockedTextsById } = useEntityTemplateLockedText();

    const isEdit = !!props.entityTemplateToEdit;
    const translationKeys = getEntityTemplateTranslationKeys(props.templateEntityType);

    const getHeader = () => {
        return translateString(isEdit ? translationKeys.editTemplate : translationKeys.createTemplate);
    };

    const [saveModel, setSaveModel] = useState<EntityTemplateSaveModel>({
        name: props.entityTemplateToEdit?.name ?? "",
        description: props.entityTemplateToEdit?.description ?? undefined,
    });
    const [showErrors, setShowErrors] = useState(false);

    // Incident logs are the only entity type with more than one standard template to start a new template from
    const choosesStandardTemplate = !isEdit && props.templateEntityType === TemplateEntityType.IncidentLog;
    const { data: standardTemplatesData, isLoading: standardTemplatesLoading } = useSelectableStandardEntityTemplates(
        props.templateEntityType,
        choosesStandardTemplate
    );
    const standardTemplates = (standardTemplatesData ?? []).map((x) => ({ id: x.id, name: x.name, disabled: !x.hasAccess }));
    const lockedStandardTemplateTexts = getLockedTextsById(standardTemplatesData ?? []);
    // The backend lists the GDPR data breach template first, which is what a new incident template used to start out as
    const selectedStandardTemplateId = saveModel.standardTemplateId ?? standardTemplates.find((x) => !x.disabled)?.id;

    const modelUpdater = {
        onNameChange: (name: string) => setSaveModel({ ...saveModel, name }),
        onDescriptionChange: (description: string) => setSaveModel({ ...saveModel, description }),
        onStandardTemplateChange: (standardTemplateId: string | null) => setSaveModel({ ...saveModel, standardTemplateId: standardTemplateId ?? undefined }),
    };

    const createMutation = useDotLegalMutation(createEntityTemplate);
    const onCreateEntityTemplate = () => {
        createMutation.mutateAsync(undefined, {
            onSuccess: (result) => {
                if (result.success()) {
                    snackbar.show(createElement(Trans, { i18nKey: "itemCreatedSuccess", values: { name: saveModel!.name } }));
                    navigate(urlProvider.getEditEntityTemplate(result.value().id));
                }
            },
        });
    };

    const editMutation = useDotLegalMutation(editEntityTemplate);
    const onEditEntityTemplate = () => {
        editMutation.mutateAsync(undefined, {
            onSuccess: (result) => {
                if (result.success()) {
                    snackbar.show(createElement(Trans, { i18nKey: "itemEditedSuccess", values: { name: saveModel!.name } }));
                    props.onTemplateSaved();
                }
            },
        });
    };

    const onSaveClick = () => {
        if (isNullOrWhitespace(saveModel.name)) {
            setShowErrors(true);
            return;
        }

        if (props.entityTemplateToEdit) {
            onEditEntityTemplate();
        } else {
            onCreateEntityTemplate();
        }
    };

    return {
        saveModel,
        modelUpdater,
        onSaveClick,
        isSaving: createMutation.isLoading,
        showErrors,
        getHeader,
        choosesStandardTemplate,
        standardTemplates,
        standardTemplatesLoading,
        lockedStandardTemplateTexts,
        selectedStandardTemplateId,
    };

    async function createEntityTemplate() {
        return await post<EntityTemplateRowViewModel>("/entity-templates?templateEntityType=" + props.templateEntityType, {
            ...saveModel,
            standardTemplateId: selectedStandardTemplateId,
        });
    }

    async function editEntityTemplate() {
        return await put<EntityTemplateRowViewModel>(`/entity-templates/${props.entityTemplateToEdit!.id}`, saveModel);
    }
}
